import { MixConfig } from '../types/config'
import { FontFaceSource } from '../types/font'

import { getFontFaceSources, getFontFaceStyles, getFontFallbackStyles, getFontStack } from './fonts'

export const formatFontNameToFile = (name: string) => {
  return name.toLowerCase().replaceAll(' ', '-')
}

const getFormattedFontNames = (mixConfig: MixConfig) => {
  const fontFamily = mixConfig.frontSettings.fontFamily || ''
  const headingFontFamily = mixConfig.frontSettings.headingFontFamily || ''

  return {
    formattedFontName: formatFontNameToFile(fontFamily),
    formattedHeadingFontName: formatFontNameToFile(headingFontFamily)
  }
}

const getUniqueFontNames = (mixConfig: MixConfig) => {
  const { formattedFontName, formattedHeadingFontName } = getFormattedFontNames(mixConfig)
  return Array.from(new Set([formattedFontName, formattedHeadingFontName].filter(Boolean)))
}

export const getFontCssObject = (mixConfig: MixConfig) => {
  const { formattedFontName, formattedHeadingFontName } = getFormattedFontNames(mixConfig)

  const fontFacesStr = getUniqueFontNames(mixConfig)
    .map((name) => getFontFaceStyles(name))
    .join('\n')

  const headingFontName = formattedHeadingFontName || formattedFontName
  const bodyVar = formattedFontName ? `--f-body: ${getFontStack(formattedFontName)};` : ''
  const headingVar = headingFontName ? `--f-heading: ${getFontStack(headingFontName)};` : ''

  return { fontFacesStr, bodyVar, headingVar }
}

export const SSR_FONTS_STYLE_ID = 'ssr-css-fonts'

export const FULL_STYLESHEET_ANCHOR_ID = 'ssr-css-full-stylesheet'

export const FONT_FACES_STYLE_ID = 'css-font-faces'

export const getFontVariablesCss = (mixConfig: MixConfig): string => {
  const { fontFacesStr, bodyVar, headingVar } = getFontCssObject(mixConfig)

  if (!fontFacesStr) return ''

  const fallbackFaces = getUniqueFontNames(mixConfig).map(getFontFallbackStyles).join('\n')

  return `${fallbackFaces}
:root {
  ${bodyVar}
  ${headingVar}
}`
}

export const getConfigFontFaceSources = (mixConfig: MixConfig): FontFaceSource[] =>
  getUniqueFontNames(mixConfig).flatMap(getFontFaceSources)

export const getFontVariablesStyle = (mixConfig: MixConfig): string => {
  const content = getFontVariablesCss(mixConfig)

  return content
    ? `
        <style id="${SSR_FONTS_STYLE_ID}">
          ${content}
        </style>
      `
    : ''
}

export const darkenHex = (hex: string, percent: number): string => {
  if (!hex) return ''

  const cleanHex = hex.length === 9 ? hex.slice(0, 7) : hex

  const fullHex = cleanHex.replace(
    /^#?([a-f\d])([a-f\d])([a-f\d])$/i,
    (_, r, g, b) => `#${r}${r}${g}${g}${b}${b}`
  )

  const bigint = parseInt(fullHex.replace('#', ''), 16)
  let r = (bigint >> 16) & 255
  let g = (bigint >> 8) & 255
  let b = bigint & 255

  const factor = 1 - percent / 100
  r = Math.max(0, Math.floor(r * factor))
  g = Math.max(0, Math.floor(g * factor))
  b = Math.max(0, Math.floor(b * factor))

  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`
}

export const getAccessibleTextColor = (hexBgColor: string) => {
  const r = parseInt(hexBgColor.slice(1, 3), 16) / 255
  const g = parseInt(hexBgColor.slice(3, 5), 16) / 255
  const b = parseInt(hexBgColor.slice(5, 7), 16) / 255

  const linear = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4))

  const luminance = 0.2126 * linear(r) + 0.7152 * linear(g) + 0.0722 * linear(b)

  const whiteContrast = (1 + 0.05) / (luminance + 0.05)
  const blackContrast = (luminance + 0.05) / 0.05

  return whiteContrast >= blackContrast ? '#FFFFFF' : '#000000'
}
